import Bowser from 'bowser';
import {CLICK_LOG_TYPE} from 'constants/config';
import {cookie} from '@afreecatv/domains';

/**
 * 시,분,초 표시할 때 앞에 0붙이는 함수
 * @param n 숫자
 * @param width pad할 길이
 */
const pad = (n: number, width: number) => {
  const str = n + '';
  return str.length >= width
    ? n
    : new Array(width - str.length + 1).join('0') + n;
};

/**
 * 시/분/초를 분/초로 변환
 * @param time HH:MM:SS
 */
export const convertTimeToMinuteSecond = (time: string) => {
  const splitTime = time.split(':');
  if (splitTime[0] === '00') {
    time = `${splitTime[1]}:${splitTime[2]}`;
  }
  return time;
};

/**
 * 초 to 시간
 * @param time 시간(seconds)
 * @param useMillisecond
 */
export const convertSecondToTime = (
  time: number = 0,
  useMillisecond: boolean = false,
) => {
  const secNum = time;
  const hours = Math.floor(secNum / 3600);
  const minutes = Math.floor((secNum - hours * 3600) / 60);
  const seconds = Math.floor(secNum - hours * 3600 - minutes * 60);

  let textHour = '00';
  let textMinute = '00';
  let textSecond = '00';

  if (hours < 10) {
    textHour = `0${hours}`;
  } else {
    textHour = `${hours}`;
  }
  if (minutes < 10) {
    textMinute = `0${minutes}`;
  } else {
    textMinute = `${minutes}`;
  }
  if (seconds < 10) {
    textSecond = `0${seconds}`;
  } else {
    textSecond = `${seconds}`;
  }

  let textTime = `${textHour}:${textMinute}:${textSecond}`;

  if (useMillisecond) {
    const milleSecond: string = secNum.toFixed(2).split('.')[1];
    return (textTime += `.${milleSecond.substring(0, 1)}`);
  }

  return textTime;
};

/**
 * 시간 to 초
 * @param time "00:00:00"
 * @param useMillisecond ms여부
 */
export const convertTimeToSecond = (
  time: string = '',
  useMillisecond: boolean = false,
) => {
  const timeSplit = time.split(':');
  const milliSecond = `0.${timeSplit[1]}`;
  let seconds = 0;

  seconds += parseInt(timeSplit[0]) * 3600;
  seconds += parseInt(timeSplit[1]) * 60;
  seconds += parseInt(timeSplit[2]);

  if (useMillisecond) {
    seconds += parseFloat(milliSecond);
  }

  return seconds;
};

/**
 * 시간 맞게 떨어지도록 지정
 * ex) "02:15:00" -> "02:00:00"
 * @param time 시간
 */
export const roundTime = (time: string) => {
  const [h, m, s] = time.split(':');
  // +연산자는 = parseInt와 동일
  const hours = +h,
    minutes = +m,
    seconds = +s;

  let calcTime = 0;
  let round = 60;
  if (hours > 0) {
    calcTime = hours * 60 + minutes; // 시간 -> 분
    const rounded = Math.floor(calcTime / round) * round;
    const rH = Math.floor(rounded / 60);
    return `${pad(rH, 2)}:00:00`;
  } else if (minutes > 0) {
    calcTime = minutes * 60 + seconds; // 분 -> 초
    const rounded = Math.floor(calcTime / round) * round;
    const rM = Math.floor(rounded / 60);
    return `00:${rM}:00`;
  } else {
    calcTime = seconds;
    round = 10;
    const rS = Math.floor(calcTime / round) * round;
    return `00:00:${rS}`;
  }
};

/**
 * DOM 요소 높이 얻어옴
 * jquery의 height()와 동일
 * @param element DOM element
 */
export const getHeight = (element: HTMLElement): number => {
  if (!element) {
    return 0;
  } else {
    const eleHeight = getComputedStyle(element, null).height;
    if (!eleHeight) {
      return 0;
    } else {
      return parseFloat(eleHeight.replace('px', ''));
    }
  }
};

/**
 * DOM 요소 너비 얻어옴
 * jquery의 width()와 동일
 * @param element DOM element
 */
export const getWidth = (element: HTMLElement): number => {
  if (!element) {
    return 0;
  } else {
    const eleWidth = getComputedStyle(element, null).width;
    if (!eleWidth) {
      return 0;
    } else {
      return parseFloat(eleWidth.replace('px', ''));
    }
  }
};

/**
 * DOM 요소 left 얻어옴
 * @param element DOM element
 */
export const getStyleLeft = (element: HTMLElement): number => {
  if (!element) {
    return 0;
  } else {
    const eleWidth = getComputedStyle(element, null).left;
    if (!eleWidth) {
      return 0;
    } else {
      return parseFloat(eleWidth.replace('px', ''));
    }
  }
};

/**
 * % -> 시간
 * @param totalDuration 미디어 전체 재생 시간(seconds)
 * @param position 현재 포지션(%)
 */
export const getCurrentTimeByPosition = (
  startTime: number,
  endTime: number,
  position: number,
) => {
  // seconds 단위
  return Number(
    ((endTime - startTime) * (position / 100) + startTime).toFixed(4),
  );
};

/**
 * 재생 바
 * 시간 -> %
 * @param time 현재 재생 시간(seconds)
 * @param totalDuration VOD 전체 시간(seconds)
 */
export const getProgressBarPostitionByTime = (
  time: number,
  totalDuration: number,
): number => {
  let position = Number(((time / totalDuration) * 100).toFixed(4));

  if (position >= 100) {
    position = 100;
  }

  if (!position) {
    position = 0;
  }

  return position;
};

/**
 * 현재 마우스 포지션 -> 시간(secodns)
 * @param e 마우스 이벤트
 * @param element DOM 요소
 * @param totalDuration 미디어 전체 시간(secodns)
 */
export const getProgressBarTimeByHandler = (
  e: MouseEvent | React.MouseEvent<HTMLButtonElement, MouseEvent>,
  element: HTMLElement,
  startTime: number,
  endTime: number,
) => {
  const progressBarWidth = getWidth(element);
  const currentX = e.clientX - element.getBoundingClientRect().left;
  let position = (currentX / progressBarWidth) * 100;

  position = Math.min(100, position);
  position = Math.max(0, position);

  const time = getCurrentTimeByPosition(startTime, endTime, position);
  return time;
};

/**
 * px -> 시간(seconds)
 * @param px 시간으로 변환할 px
 * @param width 계산할 element 너비
 * @param startTime 계산할 startTime
 * @param endTime 계산할 endtTime
 */
export const getProgressBarTimeByPx = (
  px: number,
  width: number,
  startTime: number,
  endTime: number,
) => {
  let position = (px / width) * 100;

  position = Math.min(100, position);
  position = Math.max(0, position);

  const time = getCurrentTimeByPosition(startTime, endTime, position);
  return time;
};

/**
 * 재생 바
 * % -> px 단위로 변환
 */
export const getProgressPixelByPosition = (
  element: HTMLElement,
  percent: number,
) => {
  const width = getWidth(element);
  let px = Math.round(width * (percent / 100));

  if (!px) {
    px = 0;
  }
  return px;
};

/**
 * 시간 -> px
 * @param width Element 너비
 * @param time 시간(seconds)
 * @param endTime Element 끝 시간(seconds)
 * @return px number
 */
export const getPixelByTime = (
  width: number,
  time: number,
  endTime: number,
) => {
  const px = Math.round(width * (time / endTime));
  return px <= 0 ? 0 : px;
};

/**
 * css style transform: translateX 값 얻어오는 함수
 * @param element HTMLElement
 */
export const getTranslateX = (element: HTMLDivElement) => {
  const style = window.getComputedStyle(element);
  const matrix = new WebKitCSSMatrix(style.webkitTransform);

  return matrix.m41;
};

/**
 * UI 스타일 셋팅용
 * @param element DOM
 * @param attrs 스타일 셋팅 속성들
 */
export const setAttributes = (element: HTMLElement, attrs: any) => {
  for (const key in attrs) {
    element.setAttribute(key, attrs[key]);
  }
};

/**
 * 쿠키 get 함수
 * @param {string} cname 쿠키명
 * @return {string} 쿠키 값 or ''
 */
export const getCookie = (cname: string) => {
  const name = `${cname}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
};

/**
 * 로그인 했는지 안했는지 여부
 * @return {boolean}
 */
export const isLogin = () => {
  const ticket = getCookie(cookie.TICKET_COOKIE_NAME);
  return ticket === '' ? false : true;
};

/**
 * 로그인 아이디 가져옴
 * @return {*} 로그인: ID, 비로그인: false(boolean)
 */
export const getLoginId = () => {
  const ticket = decodeURIComponent(getCookie(cookie.USER_COOKIE_NAME)) || '';
  if (!isLogin() || '' === ticket) {
    return '';
  } else {
    let userId = '';
    for (const item of ticket.split('&')) {
      if (/^(uid=)/.test(item)) {
        userId = item.replace(/^(uid=)/, '');
      }
    }
    return userId === '' ? '' : userId;
  }
};

/**
 * 근사 값 찾는 함수
 * @param data 데이터 배열
 * @param target 찾을 근사 값
 */
export const getNearValue = (data: Array<number>, target: number) => {
  let near = 0;
  let abs = 0;
  let min = Math.max(...data); // 해당 범위에서 가장 큰 값으로 지정

  for (let i = 0; i < data.length; i++) {
    abs = data[i] - target < 0 ? -(data[i] - target) : data[i] - target;
    if (abs < min) {
      min = abs;
      near = data[i];
    }
  }

  return near;
};

/**
 * 허용 브라우저 체크
 */
export const isCheckBrowser = () => {
  const userAgent = window.navigator.userAgent;
  const browser = Bowser.getParser(userAgent);
  const name = browser.getBrowserName();

  switch (name.toLowerCase()) {
    case 'chrome':
    case 'naver whale browser':
      return true;
    case 'microsoft edge':
      // 크로미움 기반 Edge는 userAgent에 edg / 예전 EdgeHTML은 edge로 붙음
      if (userAgent.toLowerCase().indexOf('edge') > -1) {
        return false;
      } else {
        // 크로미움 기반 Edge는 return true
        return true;
      }
    default:
      return false;
  }
};

/**
 * 허용 플랫폼 체크
 * 모바일은 X
 */
export const isCheckPlatform = () => {
  const browser = Bowser.getParser(window.navigator.userAgent);
  const platform = browser.getPlatformType(true);

  if (platform === 'desktop') {
    return true;
  } else {
    return false;
  }
};

/**
 * 모달 창 띄울 때 margin값 계산
 * @param element DOM
 */
export const getModalAttribute = (element: HTMLElement) => {
  const width = element.offsetWidth / 2;
  const height = element.offsetHeight / 2;
  const obj = {
    width,
    height,
  };

  return obj;
};

/**
 * 클릭 로그 보내는 함수
 * //static.afreecatv.com/asset/service/common/common_analysis.js
 * 위 소스에 oAnalysisUtil 정의되어 있음(oAnalysisUtil 변수 있을 때만 로그 보냄)
 * @param clickLogParam 로그 파라미터
 */
export const setClickLog = (clickLogParam: string) => {
  if (typeof oAnalysisUtil !== 'undefined') {
    oAnalysisUtil.setClickLog(CLICK_LOG_TYPE, clickLogParam);
  }
};

/**
 * 다차원 배열 1차원 배열로 머지
 * @param arr 다차원 배열
 */
export const mergeDedupe = (arr: any): any => {
  return [...new Set([].concat(...arr))];
};
